import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Card, Core } from '@adyen/adyen-web/auto';
import { IonContent, IonSkeletonText } from '@ionic/react';
import { IModal } from '../../router/ModalRouter';
import useBillingInfo from '../../../../hooks/payment/useBillingInfo';

export interface ImperativeAddCard {
  addCard: () => Promise<void>;
}

interface Props extends Partial<IModal> {
  adyenPromise: Promise<Core>;
}

const Content = forwardRef<ImperativeAddCard, Props>((props, ref) => {
  const { adyenPromise } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const cardElementRef = useRef<InstanceType<typeof Card> | null>(null);
  const [loading, setLoading] = useState(true);
  const { billingFullName, adyenAddressFormat } = useBillingInfo();

  useImperativeHandle(
    ref,
    () => ({
      addCard: async () => {
        cardElementRef.current?.submit();
      },
    }),
    [],
  );

  useEffect(() => {
    let mounted = true;

    const initCard = async () => {
      if (!containerRef.current || !adyenPromise) return;

      const checkout = await adyenPromise;

      if (!mounted || !containerRef.current) return;

      const card = new Card(checkout, {
        hasHolderName: true,
        holderNameRequired: true,
        billingAddressRequired: true,
        billingAddressMode: 'full',
        data: {
          holderName: billingFullName,
          billingAddress: adyenAddressFormat,
        },
      });

      card.mount(containerRef.current);
      cardElementRef.current = card;
      setLoading(false);
    };

    initCard();

    return () => {
      mounted = false;
      if (cardElementRef.current) {
        cardElementRef.current.unmount();
        cardElementRef.current = null;
      }
    };
  }, [adyenPromise]);

  return (
    <IonContent className="min-h-[75dvh] [&::part(scroll)]:py-4 space-y-md">
      {loading && (
        <div className="flex flex-col gap-4 p-sm">
          <IonSkeletonText animated={true} className="w-full h-6" />
          <IonSkeletonText animated={true} className="w-full h-6" />
          <IonSkeletonText animated={true} className="w-full h-6" />
          <IonSkeletonText animated={true} className="w-full h-6" />
          <IonSkeletonText animated={true} className="w-full h-6" />
        </div>
      )}
      <div ref={containerRef} className="p-sm pb-[200px]" />
    </IonContent>
  );
});

export default Content;
